import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { AssetCategory } from '@common/enums';
import { attachmentLimit } from '@common/validation/asset';

export type DatabaseAsset = {
    category?: AssetCategory | null;
    filename: string;
    id: string;
    s3Id?: never;
};

export type S3Asset = {
    category?: AssetCategory;
    filename: string;
    id?: never;
    s3Id: string;
};

// This is actually agnostic of ProcessActivity, but more accurate than `src/frontend/components/interfaces/IAsset.ts`
export type ProcessActivityAsset = DatabaseAsset | S3Asset;

export function getProcessActivityAssetsSchema({
    category,
    max,
    min,
}: {
    category?: AssetCategory;
    max?: number;
    min?: number;
} = {}): Joi.ArraySchema<ProcessActivityAsset[]> {
    const processActivityAssetSchema = Koi.object({
        category: Koi.koi().enum(AssetCategory).allow(null).optional(),
        filename: Koi.string().max(255),
        id: Koi.string().uuid().optional(),
        s3Id: Koi.string().max(255).optional(),
    }).xor('id', 's3Id');

    const countValidAssets = (_assets: ProcessActivityAsset[], helpers: Joi.CustomHelpers) => {
        const categoryAssets = (helpers.state.ancestors[0].assets as ProcessActivityAsset[]).filter((asset) =>
            category ? asset.category === category : true
        );
        const nonRemovedAssets = categoryAssets.filter((asset) => asset.category !== AssetCategory.removed);

        if (nonRemovedAssets.length < (min ?? 0)) {
            return helpers.error('array.min', { limit: min });
        }
        if (nonRemovedAssets.length > (max ?? attachmentLimit)) {
            return helpers.error('array.max', { limit: max });
        }

        return categoryAssets;
    };

    return Koi.array().items(processActivityAssetSchema).custom(countValidAssets);
}

/**
 * Use this function for when a conditional asset category field should be an empty array.
 */
export function getEmptyProcessActivityAssetCategorySchema(
    category: AssetCategory
): Joi.ArraySchema<ProcessActivityAsset[]> {
    return getProcessActivityAssetsSchema({ category, max: 0, min: 0 });
}

export function isDatabaseAsset(item: unknown): item is DatabaseAsset {
    return typeof item === 'object' && item !== null && 'filename' in item && 'id' in item;
}

export function isS3Asset(item: unknown): item is S3Asset {
    return typeof item === 'object' && item !== null && 'filename' in item && 's3Id' in item;
}

export function isProcessActivityAsset(item: unknown): item is ProcessActivityAsset {
    return isDatabaseAsset(item) || isS3Asset(item);
}

export function isProcessActivityAssetArray(value: unknown): value is ProcessActivityAsset[] {
    return Array.isArray(value) && value.every(isProcessActivityAsset);
}
